// src/components/TopNav/TopNav.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  useTheme,
  Box,
  Badge,
  Avatar,
  IconButton,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ProfileDrawer from '../SideNav/SideNav';
import { ProfilePath } from '../../API/config';
import { useSelector, useDispatch } from 'react-redux';
import { fetchNotifications } from './../../store/Slices/NotificationSlice';

// CSS for hollow effect for "1" and "X"
const textStyle = `
  .text-style {
    position: relative;
    display: inline-block;
    font-weight: 700;
    letter-spacing: 0.5px;
    font-size: 24px;
    color: #F3BA2F;
  }

  .text-style > span.hollow {
    -webkit-text-fill-color: transparent;
    text-fill-color: transparent;
    -webkit-text-stroke: 1.5px #F3BA2F;
    text-stroke: 1.5px #F3BA2F;
  }

  .text-style > span.solid {
    -webkit-text-fill-color: #F3BA2F;
    text-fill-color: #F3BA2F;
  }
`;

const TopNav = ({ isMobile }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const user = useSelector((state) => state.user.data);
  const { unreadMessages } = useSelector((state) => state.support);
  const { unreadCount } = useSelector((state) => state.notifications);

  useEffect(() => {
    if (user && user.id) {
      dispatch(fetchNotifications(user.id));
    }
  }, [user, dispatch]);

  const handleProfileDrawerToggle = () => {
    setProfileDrawerOpen(!profileDrawerOpen);
  };

  const profileImageUrl =
    user && user.profile_image ? `${ProfilePath}${user.profile_image}` : undefined;

  const NavButton = ({ label, onClick }) => (
    <Button
      color="inherit"
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontSize: 13,
        px: 1.6,
        minWidth: 'auto',
        color: theme.palette.text.secondary,
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
      }}
    >
      {label}
    </Button>
  );

  return (
    <>
      <style>{textStyle}</style>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.navbar,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${
            theme.palette.mode === 'dark'
              ? 'rgba(148,163,184,0.25)'
              : 'rgba(15,23,42,0.08)'
          }`,
          backdropFilter: 'blur(14px)',
          backgroundImage: 'none',
          zIndex: theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: 64,
            px: { xs: 1.5, md: 3 },
          }}
        >
          {/* Container centered like global exchanges */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 1240,
              mx: 'auto',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {isMobile ? (
              // ======= MOBILE LAYOUT =======
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                {/* Left: Avatar opens profile drawer */}
                <IconButton
                  color="inherit"
                  onClick={handleProfileDrawerToggle}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    src={profileImageUrl}
                    alt="Profile Avatar"
                    sx={{
                      width: 34,
                      height: 34,
                    }}
                  />
                </IconButton>

                {/* Center: compact logo + small tagline */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    className="text-style"
                    sx={{
                      fontSize: 18,
                      cursor: 'pointer',
                      lineHeight: 1,
                    }}
                    onClick={() => navigate('/home')}
                  >
                    <span className="hollow">1</span>
                    <span className="solid">Crypto</span>
                    <span className="hollow">X</span>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.2,
                      fontSize: 9.8,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    Trade · Futures · AI
                  </Typography>
                </Box>

                {/* Right: notifications + support */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/notifications')}
                  >
                    <Badge
                      badgeContent={unreadCount}
                      color="error"
                      overlap="circular"
                      invisible={!unreadCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          borderRadius: '50%',
                          minWidth: 18,
                          height: 18,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: theme.palette.error.main,
                          color: theme.palette.common.white,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.35)',
                        },
                      }}
                    >
                      <NotificationsIcon
                        sx={{ fontSize: 24, color: theme.palette.text.primary }}
                      />
                    </Badge>
                  </IconButton>

                  <IconButton
                    color="inherit"
                    onClick={() =>
                      user && user.role === 'Support'
                        ? navigate('/staff/support-dashboard')
                        : navigate('/support/create')
                    }
                  >
                    <Badge
                      variant="dot"
                      color="error"
                      invisible={unreadMessages === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          boxShadow: '0 0 0 1px #000',
                        },
                      }}
                    >
                      <SupportAgentOutlinedIcon
                        sx={{ fontSize: 24, color: theme.palette.text.primary }}
                      />
                    </Badge>
                  </IconButton>
                </Box>
              </Box>
            ) : (
              // ======= DESKTOP LAYOUT =======
              <>
                {/* LEFT: Logo */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="h5"
                    className="text-style"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      fontSize: 22,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => navigate('/home')}
                  >
                    <span className="hollow">1</span>
                    <span className="solid">Crypto</span>
                    <span className="hollow">X</span>
                  </Typography>

                  {/* Small tagline */}
                  {/* <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: 9.5,
                      letterSpacing: 0.6,
                      paddingTop:1,
                      textTransform: 'uppercase',
                    }}
                  >
                    Global Trading Platform
                  </Typography> */}
                </Box>

                {/* CENTER: Nav pill group */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      borderRadius: 999,
                      px: 1.8,
                      py: 0.6,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15,23,42,0.9)'
                          : 'rgba(248,250,252,0.96)',
                      border: `1px solid ${
                        theme.palette.mode === 'dark'
                          ? 'rgba(148,163,184,0.35)'
                          : 'rgba(148,163,184,0.3)'
                      }`,
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 10px 30px rgba(0,0,0,0.85)'
                          : '0 8px 24px rgba(15,23,42,0.12)',
                    }}
                  >
                    <NavButton label="Home" onClick={() => navigate('/home')} />
                    <NavButton label="Spot" onClick={() => navigate('/spot')} />
                    <NavButton
                      label="Futures"
                      onClick={() => navigate('/futures')}
                    />
                    <NavButton
                      label="AI Trading"
                      onClick={() => navigate('/ai-trading')}
                    />
                    <NavButton
                      label="Assets"
                      onClick={() => navigate('/assets')}
                    />
                  </Box>
                </Box>

                {/* RIGHT: notifications, support, avatar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                  }}
                >
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/notifications')}
                  >
                    <Badge
                      badgeContent={unreadCount}
                      color="error"
                      overlap="circular"
                      invisible={!unreadCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          borderRadius: '50%',
                          minWidth: 18,
                          height: 18,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: theme.palette.error.main,
                          color: theme.palette.common.white,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.35)',
                        },
                      }}
                    >
                      <NotificationsIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.text.primary,
                        }}
                      />
                    </Badge>
                  </IconButton>

                  <IconButton
                    color="inherit"
                    onClick={() =>
                      user && user.role === 'Support'
                        ? navigate('/staff/support-dashboard')
                        : navigate('/support/create')
                    }
                  >
                    <Badge
                      variant="dot"
                      color="error"
                      invisible={unreadMessages === 0}
                    >
                      <SupportAgentOutlinedIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.text.primary,
                        }}
                      />
                    </Badge>
                  </IconButton>

                  <IconButton
                    color="inherit"
                    onClick={handleProfileDrawerToggle}
                    sx={{ ml: 0.2 }}
                  >
                    <Avatar
                      src={profileImageUrl}
                      alt="Profile Avatar"
                      sx={{
                        width: 34,
                        height: 34,
                        boxShadow:
                          theme.palette.mode === 'dark'
                            ? '0 0 0 1px rgba(148,163,184,0.7)'
                            : '0 0 0 1px rgba(15,23,42,0.08)',
                      }}
                    />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Toolbar>

        {/* خط ذهبي رفيع أسفل الـ TopNav لإحساس منصات عالمية */}
        <Box
          sx={{
            height: 2,
            background:
              'linear-gradient(90deg, rgba(243,186,47,0.1), rgba(243,186,47,0.9), rgba(243,186,47,0.1))',
          }}
        />
      </AppBar>

      <ProfileDrawer open={profileDrawerOpen} onClose={handleProfileDrawerToggle} />
    </>
  );
};

export default TopNav;
